import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import CreateNoteModal from '../components/notes/CreateNoteModal';
import NoteCard from '../components/notes/NoteCard';
import NoteDetailModal from '../components/notes/NoteDetailModal';
import LinkTimeEntryModal from '../components/notes/LinkTimeEntryModal';
import { FileText, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AnimatedLogoRolodex } from '../components/ui/animated-logo-rolodex';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const Notes = () => {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [linkTimeEntryNote, setLinkTimeEntryNote] = useState(null);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    limit: 20,
    offset: 0,
    project_id: '',
    start_date: '',
    end_date: '',
    has_time_entry: undefined,
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [totalNotes, setTotalNotes] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (user) {
      fetchProjects();
      fetchNotes();
    }
  }, [user, filters]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchNotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);
      if (filters.project_id) params.append('project_id', filters.project_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.has_time_entry !== undefined) params.append('has_time_entry', filters.has_time_entry);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${API}/notes?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotes(response.data.notes || []);
      setTotalNotes(response.data.total || 0);
      setHasMore(response.data.has_more || false);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setError('Failed to load notes');
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async (audioBlob, data) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('audio', audioBlob, 'note.webm');
      formData.append('meeting_date', data.meeting_date);
      if (data.meeting_time) formData.append('meeting_time', data.meeting_time);
      if (data.project_id) formData.append('project_id', data.project_id);
      if (data.title) formData.append('title', data.title);
      if (data.create_time_entry) formData.append('create_time_entry', 'true');
      if (data.time_entry_duration) formData.append('time_entry_duration', data.time_entry_duration);
      if (data.tags && data.tags.length > 0) formData.append('tags', data.tags.join(','));

      const response = await axios.post(`${API}/notes`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Note created successfully!');
      await fetchNotes();
      return response.data;
    } catch (error) {
      console.error('Error creating note:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create note';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Note deleted successfully');
      setNotes(notes.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  const handleViewDetails = async (noteId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedNote(response.data.note);
    } catch (error) {
      console.error('Error fetching note details:', error);
      toast.error('Failed to load note details');
    }
  };

  const handleNoteUpdate = async () => {
    // Refetch the selected note if it's open
    if (selectedNote && selectedNote.id) {
      await handleViewDetails(selectedNote.id);
    }
    // Also refresh the notes list
    await fetchNotes();
  };

  const handleLinkTimeEntry = (noteId) => {
    // Find the note and open link modal
    const note = notes.find(n => n.id === noteId);
    if (note) {
      setLinkTimeEntryNote(note);
      setIsLinkModalOpen(true);
    }
  };

  const handleLinkSuccess = () => {
    fetchNotes(); // Refresh notes list
    // Trigger refresh of time entries on home and calendar pages
    window.dispatchEvent(new CustomEvent('timeEntriesUpdated'));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: key === 'offset' ? value : 0 // Only keep offset if explicitly setting it, otherwise reset
    }));
  };

  const clearFilters = () => {
    setFilters({
      limit: 20,
      offset: 0,
      project_id: '',
      start_date: '',
      end_date: '',
      has_time_entry: undefined,
      search: ''
    });
  };

  const getDateFilterOptions = () => {
    // Get local date components (not UTC) to avoid timezone shifts
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    
    // Create date strings in YYYY-MM-DD format using local date
    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    const todayLocal = new Date(year, month, day);
    const weekAgo = new Date(year, month, day - 7);
    const monthAgo = new Date(year, month, day - 30);

    return {
      today: formatDate(todayLocal),
      week: formatDate(weekAgo),
      month: formatDate(monthAgo)
    };
  };

  const applyDateFilter = (period) => {
    // Get current local date in YYYY-MM-DD format
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    const todayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    const dates = getDateFilterOptions();
    
    if (period === 'today') {
      handleFilterChange('start_date', dates.today);
      handleFilterChange('end_date', todayStr);
    } else if (period === 'week') {
      handleFilterChange('start_date', dates.week);
      handleFilterChange('end_date', todayStr);
    } else if (period === 'month') {
      handleFilterChange('start_date', dates.month);
      handleFilterChange('end_date', todayStr);
    } else {
      handleFilterChange('start_date', '');
      handleFilterChange('end_date', '');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Loading user information...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-slate-900 dark:to-slate-800 transition-colors">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-md border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <AnimatedLogoRolodex size="small" />
              <div>
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>
                  Notes
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 transition-colors">
                  Voice notes with AI summaries
                </p>
              </div>
            </div>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
            >
              <span className="text-xl mr-2">🎤</span>
              New Note
            </Button>
          </div>

          {/* Search Bar */}
          <div className="flex gap-2 mb-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4 transition-colors" />
              <Input
                type="search"
                placeholder="Search notes..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filters
            </Button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3 mb-3 transition-colors">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Project
                  </label>
                  <select
                    value={filters.project_id}
                    onChange={(e) => handleFilterChange('project_id', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 transition-colors"
                  >
                    <option value="">All Projects</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
                    Time Period
                  </label>
                  <select
                    onChange={(e) => applyDateFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:focus:ring-cyan-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 transition-colors"
                  >
                    <option value="">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 transition-colors">
                  <input
                    type="checkbox"
                    checked={filters.has_time_entry === true}
                    onChange={(e) => handleFilterChange('has_time_entry', e.target.checked ? true : undefined)}
                    className="w-4 h-4 text-cyan-600 dark:text-cyan-400"
                  />
                  Has Time Entry
                </label>
                {(filters.project_id || filters.start_date || filters.has_time_entry) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="ml-auto text-xs"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 dark:border-cyan-400"></div>
            <p className="text-gray-600 dark:text-gray-400 mt-4 transition-colors">Loading notes...</p>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4 transition-colors">
            <p className="text-red-800 dark:text-red-300 transition-colors">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && notes.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2 transition-colors">
              No notes yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 transition-colors">
              Tap the 🎤 button to record your first note.<br />
              Great for meeting summaries, daily logs, and ideas!
            </p>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
            >
              🎤 Create Your First Note
            </Button>
          </div>
        )}

        {/* Notes List */}
        {!loading && !error && notes.length > 0 && (
          <>
            <div className="space-y-4">
              {notes.map((note) => (
                <NoteCard
                  key={note.id}
                  note={note}
                  onViewDetails={() => handleViewDetails(note.id)}
                  onDelete={() => handleDeleteNote(note.id)}
                  onLinkTimeEntry={() => handleLinkTimeEntry(note.id)}
                />
              ))}
            </div>

            {/* Pagination */}
            <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4 transition-colors">
              <div className="text-sm text-gray-600 dark:text-gray-400 transition-colors">
                Showing {notes.length} of {totalNotes} notes
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newOffset = Math.max(0, filters.offset - filters.limit);
                    handleFilterChange('offset', newOffset);
                  }}
                  disabled={filters.offset === 0 || loading}
                  className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <div className="text-sm text-gray-600 dark:text-gray-400 px-3 transition-colors">
                  Page {Math.floor(filters.offset / filters.limit) + 1} of {Math.ceil(totalNotes / filters.limit) || 1}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newOffset = filters.offset + filters.limit;
                    handleFilterChange('offset', newOffset);
                  }}
                  disabled={!hasMore || loading}
                  className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      <CreateNoteModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateNote}
        onComplete={() => fetchNotes()}
        projects={projects}
      />

      <NoteDetailModal
        note={selectedNote}
        isOpen={!!selectedNote}
        onClose={() => setSelectedNote(null)}
        onUpdate={handleNoteUpdate}
      />

      <LinkTimeEntryModal
        isOpen={isLinkModalOpen}
        onClose={() => {
          setIsLinkModalOpen(false);
          setLinkTimeEntryNote(null);
        }}
        note={linkTimeEntryNote}
        projects={projects}
        onSuccess={handleLinkSuccess}
      />

      {/* Bottom Navigation */}
      <BottomNav active="notes" />
    </div>
  );
};

export default Notes;

